import React, { useCallback, useRef, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import { Customer, Order } from "../../types";
import { orderService } from "../../services/orderService";
import { customerService } from "../../services/customerService";
import { OrderForm, OrderFormPrefill } from "../forms/OrderForm";
import { CourierOrderForm } from "../forms/CourierOrderForm";
import { OrderStatusManager } from "./OrderStatusManager";
import { PaymentUpdateDialog } from "./PaymentUpdateDialog";
import { ReceiptModal } from "../../pages/pos/components/ReceiptModal";

interface UseOrderActionsOptions {
    /** An existing order changed here (edit saved, status changed, payment
     * collected, courier order created) — always a freshly fetched order. */
    onOrderUpdated?: (order: Order) => void;
    /** A new order was created (Add, Import or Clone). */
    onOrderCreated?: (order: Order) => void;
    onError?: (message: string) => void;
}

/** Spread into <OrderDetailDrawer> so every screen wires it the same way. */
export interface OrderDrawerActionProps {
    onPrintReceipt: (orderId: string, autoPrint?: boolean) => void;
    onUpdateStatus: (order: Order) => void;
    onCreateCourier: (order: Order) => void;
    onDuplicateOrder: (order: Order) => void;
    /** The drawer's quick edits saved a change — `order` is freshly re-read. */
    onOrderChanged: (order: Order) => void;
}

type FormState = { open: boolean; mode: "create" | "edit"; order: Order | null; prefill: OrderFormPrefill | null };
type CourierState = { open: boolean; order: Order | null; customer: Customer | null };

const CLOSED_FORM: FormState = { open: false, mode: "create", order: null, prefill: null };

/**
 * The order actions shared by every screen that shows orders — receipt,
 * add/import, edit, clone, status change (incl. collecting the balance when
 * an unpaid order is marked delivered) and courier — plus the dialogs they
 * open. All Orders and the Delivery Planner both use this, so their order
 * drawers and receipts can't drift apart again.
 *
 * Every action re-fetches the full order first: list rows and receipts are
 * partial shapes (no payments, no ids), and forms or the underpaid check
 * silently misbehave on partial data.
 */
export function useOrderActions({ onOrderUpdated, onOrderCreated, onError }: UseOrderActionsOptions = {}) {
    const [busy, setBusy] = useState(false);
    const [receipt, setReceipt] = useState<{ order: Order | null; open: boolean; autoPrint: boolean }>({
        order: null,
        open: false,
        autoPrint: false,
    });
    const [form, setForm] = useState<FormState>(CLOSED_FORM);
    const [statusOrder, setStatusOrder] = useState<Order | null>(null);
    const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
    const [courier, setCourier] = useState<CourierState>({ open: false, order: null, customer: null });

    // Latest callbacks without re-creating every action when a screen re-renders.
    const callbacks = useRef({ onOrderUpdated, onOrderCreated, onError });
    callbacks.current = { onOrderUpdated, onOrderCreated, onError };

    const reportError = (message: string) => callbacks.current.onError?.(message);

    const fetchFull = useCallback(async (order: Order | string): Promise<Order | null> => {
        const id = typeof order === "string" ? order : order.order_id;
        try {
            return await orderService.getOrderById(id);
        } catch {
            return typeof order === "string" ? null : order;
        }
    }, []);

    /** Runs `action` with the full order, showing the busy backdrop meanwhile. */
    const withFullOrder = useCallback(
        async (order: Order | string, action: (full: Order) => void) => {
            setBusy(true);
            try {
                const full = await fetchFull(order);
                if (full) action(full);
                else reportError("Couldn't load the order. Please try again.");
            } finally {
                setBusy(false);
            }
        },
        [fetchFull],
    );

    /** Re-fetch after a change and hand the fresh order to the screen. */
    const notifyUpdated = useCallback(
        async (orderId: string | undefined) => {
            if (!orderId) return;
            const fresh = await fetchFull(orderId);
            if (fresh) callbacks.current.onOrderUpdated?.(fresh);
        },
        [fetchFull],
    );

    // ── Actions ─────────────────────────────────────────────────────────

    const openReceipt = useCallback(async (orderId: string, autoPrint = false) => {
        setBusy(true);
        try {
            const order = await orderService.getOrderForReceipt(orderId);
            setReceipt({ order, open: true, autoPrint });
        } catch {
            reportError("Failed to load order details for receipt");
        } finally {
            setBusy(false);
        }
    }, []);

    const createOrder = useCallback((prefill: OrderFormPrefill | null = null) => {
        setForm({ open: true, mode: "create", order: null, prefill });
    }, []);

    const editOrder = useCallback(
        (order: Order) => withFullOrder(order, (full) => setForm({ open: true, mode: "edit", order: full, prefill: null })),
        [withFullOrder],
    );

    // Clone = the create form pre-filled from this order.
    const cloneOrder = useCallback(
        (order: Order) => withFullOrder(order, (full) => setForm({ open: true, mode: "create", order: full, prefill: null })),
        [withFullOrder],
    );

    const updateStatus = useCallback(
        (order: Order | string) => withFullOrder(order, (full) => setStatusOrder(full)),
        [withFullOrder],
    );

    const createCourier = useCallback(
        (order: Order | string) =>
            withFullOrder(order, async (full) => {
                let customer: Customer | null = (full.customer as Customer | undefined) ?? null;
                if (full.customer_id) {
                    try {
                        customer = await customerService.getCustomerById(full.customer_id);
                    } catch {
                        /* fall back to the order's embedded customer */
                    }
                }
                setCourier({ open: true, order: full, customer });
            }),
        [withFullOrder],
    );

    const collectPayment = useCallback(
        (order: Order) => withFullOrder(order, (full) => setPaymentOrder(full)),
        [withFullOrder],
    );

    // ── Receipt buttons act on the receipt's order ──────────────────────

    const fromReceipt = (action: (order: Order) => void) => () => {
        const order = receipt.order;
        if (!order) return;
        setReceipt((r) => ({ ...r, open: false }));
        action(order);
    };

    // ── Dialog handlers ─────────────────────────────────────────────────

    const handleFormSubmit = (saved: Order) => {
        const wasCreate = form.mode === "create";
        setForm(CLOSED_FORM);
        if (wasCreate) callbacks.current.onOrderCreated?.(saved);
        else void notifyUpdated(saved?.order_id);
    };

    const handleCourierSuccess = async (response: any) => {
        const order = courier.order;
        const consignmentId = response?.consignment?.consignment_id != null ? String(response.consignment.consignment_id) : "";
        const trackingCode = response?.consignment?.tracking_code;
        // The server already saved the consignment on the order (same step as
        // the booking) — refresh it so tracking/label links show up.
        if (order?.order_id) void notifyUpdated(order.order_id);
        alert(
            `Courier order created!${consignmentId ? ` Consignment ID: ${consignmentId}` : ""}${trackingCode ? ` · Tracking code: ${trackingCode}` : ""}`,
        );
    };

    const orderChanged = useCallback((order: Order) => callbacks.current.onOrderUpdated?.(order), []);

    const drawerActionProps: OrderDrawerActionProps = {
        onPrintReceipt: openReceipt,
        onUpdateStatus: updateStatus,
        onCreateCourier: createCourier,
        onDuplicateOrder: cloneOrder,
        onOrderChanged: orderChanged,
    };

    const dialogs = (
        <>
            <ReceiptModal
                open={receipt.open}
                // Keep the order while the dialog animates closed.
                onClose={() => setReceipt((r) => ({ ...r, open: false, autoPrint: false }))}
                order={receipt.order}
                customer={(receipt.order?.customer as Customer | undefined) ?? null}
                paymentMethod="CASH"
                amountPaid={receipt.order?.total_amount || 0}
                onDuplicateOrder={fromReceipt(cloneOrder)}
                onEdit={fromReceipt(editOrder)}
                onUpdateStatus={fromReceipt(updateStatus)}
                onCreateCourier={fromReceipt(createCourier)}
                autoPrint={receipt.autoPrint}
            />

            <OrderForm
                open={form.open}
                onClose={() => setForm(CLOSED_FORM)}
                onSubmit={handleFormSubmit}
                order={form.order}
                mode={form.mode}
                prefill={form.prefill}
            />

            <OrderStatusManager
                open={!!statusOrder}
                onClose={() => setStatusOrder(null)}
                order={statusOrder}
                onSuccess={() => void notifyUpdated(statusOrder?.order_id)}
                onDeliveredUnpaid={collectPayment}
            />

            <PaymentUpdateDialog
                open={!!paymentOrder}
                onClose={() => setPaymentOrder(null)}
                order={paymentOrder}
                onSuccess={() => void notifyUpdated(paymentOrder?.order_id)}
            />

            {courier.order && (
                <CourierOrderForm
                    open={courier.open}
                    onClose={() => setCourier((c) => ({ ...c, open: false }))}
                    onSuccess={handleCourierSuccess}
                    order={courier.order}
                    customer={courier.customer}
                />
            )}

            <Backdrop open={busy} sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: "common.white" }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );

    return {
        busy,
        openReceipt,
        createOrder,
        editOrder,
        cloneOrder,
        updateStatus,
        createCourier,
        drawerActionProps,
        dialogs,
    };
}
